import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FilterCondition, FilterGroup } from './types/filters';
import CandidatePickerSchematic, { CANDIDATE_PICKER_COLUMNS } from './components/CandidatePickerSchematic';
import CompanyPickerSchematic, { COMPANY_PICKER_COLUMNS } from './components/CompanyPickerSchematic';
import ManagerPickerSchematic, { MANAGER_PICKER_COLUMNS, MANAGER_LONG_TAIL_COLUMNS } from './components/ManagerPickerSchematic';

interface RadiusPreview {
  location: string;
  distance: number;
  type?: 'candidate' | 'company';
}

interface AISearchPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Filter groups returned by the AI; user may edit these before applying. */
  filterGroups: FilterGroup[];
  /** The user's original natural-language query, shown at the top of the modal. */
  query: string;
  /** Human-readable explanation from the AI. */
  explanation?: string;
  /** Optional location filters returned by the AI (display-only in V1). */
  radiusSearch?: RadiusPreview;
  companyRadiusSearch?: RadiusPreview;
  candidateRadiusSearch?: RadiusPreview;
  /** Project ID — used to fetch a sample candidate/company for the visual picker. */
  projectId?: number;
  /** Called with the (possibly edited) filter groups when the user confirms. */
  onApply: (editedFilterGroups: FilterGroup[]) => void;
}

// Long-tail searchable columns per entity type that aren't surfaced in the
// visual schematic. Click-to-toggle as small chips inside "+ more fields".
const CANDIDATE_LONG_TAIL_COLUMNS: string[] = [
  'first_name', 'last_name', 'position', 'company',
  'email', 'personal_email', 'li_profile_url',
  'previous_company_1', 'previous_company_2', 'previous_company_3',
  'top_skills_5', 'all_skills_text',
  'custom_data'
];
const COMPANY_LONG_TAIL_COLUMNS: string[] = [
  'company_name', 'industry', 'subindustry',
  'description', 'specialties',
  'business_type', 'pattern_tags', 'primary_offerings',
  'formatted_address', 'office_address', 'headquarters',
  'website', 'company_domain', 'url',
  'custom_data'
];

// Pseudo-column meaning "search across ALL profile columns" (backend expands
// it, mirroring the free-text search bar). Selecting it is exclusive — it
// replaces the per-column selection.
const ANY_FIELD_COL = 'any_field';

const formatColumnLabel = (col: string): string =>
  col === ANY_FIELD_COL
    ? 'All columns'
    : col.replace(/::text$/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// Pull the displayable keyword string from a condition. Single-word conditions
// use `value` directly; multi-word "all words in same field" conditions
// (`values: ["AI", "agent"]`) get joined with a space so the chip reads as the
// original phrase ("AI agent"). Returns null for non-keyword conditions.
const keywordFromCondition = (c: FilterCondition): string | null => {
  if (c.operator !== 'contains' && c.operator !== 'equals') return null;
  if (c.values && Array.isArray(c.values) && c.values.length > 0) {
    return c.values.join(' ');
  }
  if (typeof c.value === 'string' && c.value.length > 0) {
    return c.value;
  }
  return null;
};

// Conditions that should be editable as keyword chips. Includes both
// single-word (`value`) and multi-word (`values`) phrases — the latter are
// surfaced as chips so the user can edit them via the same column picker as
// everything else, instead of being hidden under "Other rules".
const isKeywordCondition = (c: FilterCondition): boolean =>
  keywordFromCondition(c) !== null;

// All keyword values used in a group (deduped, preserves first-seen order).
// The any_field condition stores its OR phrases in `values`, each a whole
// phrase — expand them into individual chips rather than joining into one.
const getGroupKeywords = (group: FilterGroup): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  const push = (kw: string) => {
    if (kw && !seen.has(kw)) { seen.add(kw); out.push(kw); }
  };
  for (const c of group.conditions) {
    if (c.column === ANY_FIELD_COL && Array.isArray(c.values)) {
      for (const v of c.values) push(String(v));
      continue;
    }
    const kw = keywordFromCondition(c);
    if (kw !== null) push(kw);
  }
  return out;
};

// All columns used by keyword conditions in a group (deduped).
const getGroupColumns = (group: FilterGroup): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of group.conditions) {
    if (isKeywordCondition(c) && !seen.has(c.column)) {
      seen.add(c.column);
      out.push(c.column);
    }
  }
  return out;
};

// Conditions in a group that aren't simple keyword chips (multi-word rules,
// numeric comparisons, etc.). Shown in a read-only "advanced rules" list.
const getNonKeywordConditions = (group: FilterGroup): FilterCondition[] =>
  group.conditions.filter(c => !isKeywordCondition(c));

// Rebuild the condition list for a group from the (possibly edited) keyword
// chips and column selection. Each (column, keyword) pair becomes one
// condition; non-keyword conditions are preserved verbatim. Multi-word
// keywords (containing whitespace) are encoded as `values: [...words]` so the
// backend's "all words must appear in the same field" matching kicks in;
// single-word keywords use `value` and match anywhere in the field.
const rebuildGroupConditions = (
  group: FilterGroup,
  keywords: string[],
  columns: string[]
): FilterCondition[] => {
  const preserved = getNonKeywordConditions(group);
  const fresh: FilterCondition[] = [];

  // "All columns" (any_field) is exclusive and emits ONE condition holding all
  // keyword phrases in `values`. The backend ORs those phrases across every
  // profile field (each kept whole, so "mission critical" matches the phrase).
  // A single condition means the group's AND/OR logic can't accidentally AND
  // the phrases together.
  if (columns.includes(ANY_FIELD_COL)) {
    if (keywords.length > 0) {
      fresh.push({
        id: `${group.id}-any_field`,
        column: ANY_FIELD_COL,
        operator: 'contains',
        entityType: group.entityType,
        value: keywords[0], // back-compat display
        values: keywords
      });
    }
    return [...preserved, ...fresh];
  }

  let idx = 0;
  for (const col of columns) {
    for (const kw of keywords) {
      const tokens = kw.split(/\s+/).filter(Boolean);
      const base = {
        id: `${group.id}-${col}-${idx++}`,
        column: col,
        operator: 'contains' as const,
        entityType: group.entityType,
        value: kw // also stored for display/back-compat; backend uses `values` when present
      };
      if (tokens.length > 1) {
        fresh.push({ ...base, values: tokens });
      } else {
        fresh.push(base);
      }
    }
  }
  return [...preserved, ...fresh];
};

const AISearchPreviewModal: React.FC<AISearchPreviewModalProps> = ({
  isOpen,
  onClose,
  filterGroups,
  query,
  explanation,
  radiusSearch,
  companyRadiusSearch,
  candidateRadiusSearch,
  projectId,
  onApply
}) => {
  // Local editable copy. Reset whenever the modal opens with new data.
  const [editedGroups, setEditedGroups] = useState<FilterGroup[]>(filterGroups);
  const [newKeywordInput, setNewKeywordInput] = useState<Record<string, string>>({});
  const [showMoreFields, setShowMoreFields] = useState<Record<string, boolean>>({});

  // Sample candidate + company for the visual profile picker. Fetched once
  // per project when the modal opens. The "most-complete" sample comes from
  // the backend (lowest num_nulls across the visible profile fields).
  const [sampleCandidate, setSampleCandidate] = useState<any>(null);
  const [sampleCompany, setSampleCompany] = useState<any>(null);
  const [sampleManager, setSampleManager] = useState<any>(null);
  const [sampleFetchedFor, setSampleFetchedFor] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      setEditedGroups(filterGroups);
      setNewKeywordInput({});
      setShowMoreFields({});
    }
  }, [isOpen, filterGroups]);

  useEffect(() => {
    if (!isOpen || !projectId || sampleFetchedFor === projectId) return;
    const token = localStorage.getItem('token');
    fetch(`/api/ai-search/sample-entity?projectId=${projectId}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : { candidate: null, company: null, manager: null })
      .then(data => {
        setSampleCandidate(data.candidate || null);
        setSampleCompany(data.company || null);
        setSampleManager(data.manager || null);
        setSampleFetchedFor(projectId);
      })
      .catch(() => {
        // Schematics fall back to "no sample available" — non-fatal.
        setSampleFetchedFor(projectId);
      });
  }, [isOpen, projectId, sampleFetchedFor]);

  // Per-group derived view of editable chips + columns
  const groupViews = useMemo(
    () => editedGroups.map(g => ({
      group: g,
      keywords: getGroupKeywords(g),
      columns: getGroupColumns(g),
      nonKeyword: getNonKeywordConditions(g)
    })),
    [editedGroups]
  );

  const updateGroup = (groupId: string, updater: (g: FilterGroup) => FilterGroup) => {
    setEditedGroups(prev => prev.map(g => (g.id === groupId ? updater(g) : g)));
  };

  const removeKeyword = (groupId: string, keyword: string) => {
    updateGroup(groupId, g => {
      const remainingKeywords = getGroupKeywords(g).filter(k => k !== keyword);
      return { ...g, conditions: rebuildGroupConditions(g, remainingKeywords, getGroupColumns(g)) };
    });
  };

  const addKeyword = (groupId: string) => {
    const raw = (newKeywordInput[groupId] || '').trim();
    if (!raw) return;
    updateGroup(groupId, g => {
      const cols = getGroupColumns(g);
      if (cols.length === 0) return g; // no columns to apply to — shouldn't happen
      const existing = getGroupKeywords(g);
      if (existing.includes(raw)) return g;
      return { ...g, conditions: rebuildGroupConditions(g, [...existing, raw], cols) };
    });
    setNewKeywordInput(prev => ({ ...prev, [groupId]: '' }));
  };

  const toggleColumn = (groupId: string, column: string) => {
    updateGroup(groupId, g => {
      const cols = getGroupColumns(g);
      let next: string[];
      if (column === ANY_FIELD_COL) {
        // "All columns" is exclusive: on → only any_field; off → clear.
        next = cols.includes(ANY_FIELD_COL) ? [] : [ANY_FIELD_COL];
      } else {
        // Selecting a specific column drops any_field (leaves "all columns" mode).
        const base = cols.filter(c => c !== ANY_FIELD_COL);
        next = base.includes(column) ? base.filter(c => c !== column) : [...base, column];
      }
      return { ...g, conditions: rebuildGroupConditions(g, getGroupKeywords(g), next) };
    });
  };

  // Toggle the exclusive "All columns" (any_field) mode for a group.
  const toggleAllColumns = (groupId: string) => toggleColumn(groupId, ANY_FIELD_COL);

  // Used by the visual schematic when a multi-column section is clicked.
  // If ALL of the section's columns are already selected → remove them all;
  // otherwise → add all that aren't selected. This makes a section feel like
  // a single binary toggle rather than several independent toggles.
  const toggleColumnsForGroup = (groupId: string, columns: string[]) => {
    updateGroup(groupId, g => {
      // Picking specific columns via the schematic leaves "all columns" mode.
      const current = getGroupColumns(g).filter(c => c !== ANY_FIELD_COL);
      const allPresent = columns.every(c => current.includes(c));
      const next = allPresent
        ? current.filter(c => !columns.includes(c))
        : current.concat(columns.filter(c => !current.includes(c)));
      return { ...g, conditions: rebuildGroupConditions(g, getGroupKeywords(g), next) };
    });
  };

  const handleApply = () => {
    // Drop any group that ended up with zero conditions (user removed everything)
    const cleaned = editedGroups.filter(g => g.conditions.length > 0);
    onApply(cleaned);
  };

  if (!isOpen) return null;

  const radiusBlocks: { label: string; r: RadiusPreview }[] = [];
  if (radiusSearch) radiusBlocks.push({ label: radiusSearch.type === 'company' ? 'Company location' : 'Candidate location', r: radiusSearch });
  if (companyRadiusSearch) radiusBlocks.push({ label: 'Company location', r: companyRadiusSearch });
  if (candidateRadiusSearch) radiusBlocks.push({ label: 'Candidate location', r: candidateRadiusSearch });

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-modal max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl">✨</span>
              <h2 className="text-lg font-semibold text-gray-900">Review AI Search</h2>
            </div>
            <p className="text-sm text-gray-600 truncate">
              <span className="text-gray-500">Your query:</span> "{query}"
            </p>
            {explanation && (
              <p className="text-xs text-gray-500 mt-1">{explanation}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-2xl leading-none ml-4"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {groupViews.length === 0 && radiusBlocks.length === 0 && (
            <p className="text-sm text-gray-500 italic">No filters generated. Try rephrasing your query.</p>
          )}

          {groupViews.map(({ group, keywords, columns, nonKeyword }) => (
            <div key={group.id} className="mb-5 p-4 border border-gray-200 rounded-xl">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    {group.name || 'Filter group'}
                  </h3>
                  <p className="text-xs text-gray-500 capitalize">
                    {group.entityType} · matches if any keyword appears in any selected column
                  </p>
                </div>
              </div>

              {/* Editable keyword chips */}
              {keywords.length > 0 && (
                <div className="mb-3" data-tour="ai-preview-keywords">
                  <p className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-1.5">Keywords</p>
                  <div className="flex flex-wrap gap-1.5 items-center">
                    {keywords.map(kw => {
                      const tokens = kw.split(/\s+/).filter(Boolean);
                      const isMultiWord = tokens.length > 1;
                      return (
                        <span
                          key={kw}
                          title={isMultiWord ? 'All words must appear in the same field' : undefined}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${
                            isMultiWord
                              ? 'bg-purple-50 text-purple-700 border-purple-200'
                              : 'bg-blue-50 text-blue-700 border-blue-200'
                          }`}
                        >
                          {isMultiWord ? tokens.join(' + ') : kw}
                          <button
                            onClick={() => removeKeyword(group.id, kw)}
                            className={isMultiWord ? 'text-purple-400 hover:text-purple-700' : 'text-blue-400 hover:text-blue-700'}
                            aria-label={`Remove ${kw}`}
                          >
                            ✕
                          </button>
                        </span>
                      );
                    })}
                    <input
                      type="text"
                      value={newKeywordInput[group.id] || ''}
                      onChange={e => setNewKeywordInput(prev => ({ ...prev, [group.id]: e.target.value }))}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addKeyword(group.id);
                        }
                      }}
                      placeholder="+ add keyword"
                      className="px-2 py-1 text-xs border border-dashed border-gray-300 rounded-full bg-white focus:outline-none focus:border-blue-400 w-32"
                    />
                  </div>
                </div>
              )}

              {/* Visual profile picker — hover a section on the sample
                  candidate / company to see its column name, click to
                  toggle inclusion. The "Selected columns" summary above
                  is the source of truth for what gets searched. */}
              {(group.entityType === 'candidate' || group.entityType === 'company' || group.entityType === 'manager') && keywords.length > 0 && (
                <div className="mb-3">
                  {/* Selected columns summary — chips that mirror what's
                      highlighted in the visual, so the user can verify and
                      remove without scrolling. */}
                  <div className="mb-2 px-3 py-2 bg-amber-50/40 border border-amber-200 rounded-lg" data-tour="ai-preview-columns">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-[11px] font-medium text-amber-800 uppercase tracking-wide">
                        {columns.includes(ANY_FIELD_COL)
                          ? 'Searching all columns'
                          : `Searching in ${columns.length} ${columns.length === 1 ? 'column' : 'columns'}`}
                      </p>
                      {group.entityType === 'candidate' ? (
                        <button
                          type="button"
                          onClick={() => toggleAllColumns(group.id)}
                          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border transition-colors ${
                            columns.includes(ANY_FIELD_COL)
                              ? 'bg-amber-500 text-white border-amber-500'
                              : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-100'
                          }`}
                          title="Match keywords across every profile column (like the search bar). Excludes custom columns."
                        >
                          {columns.includes(ANY_FIELD_COL) ? '✓ All columns' : 'Search all columns'}
                        </button>
                      ) : (
                        <p className="text-[10px] text-gray-500 italic">Click cells below to add / remove</p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {columns.length === 0 ? (
                        <p className="text-[11px] text-gray-500 italic">No columns selected — pick at least one or this filter group will be dropped.</p>
                      ) : columns.map(col => (
                        <button
                          key={col}
                          type="button"
                          onClick={() => toggleColumn(group.id, col)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-white text-amber-800 border border-amber-300 hover:bg-amber-100"
                          title="Remove this column"
                        >
                          {formatColumnLabel(col)}
                          <span className="text-amber-400">✕</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Sample profile — same layout as the entity side panel */}
                  <div className="border border-gray-200 rounded-xl bg-white p-3" data-tour="ai-preview-sample">
                    <p className="text-[10px] text-gray-400 italic mb-2 px-2">
                      Sample {group.entityType} — hover a cell to see its column name
                    </p>
                    {group.entityType === 'candidate' ? (
                      <CandidatePickerSchematic
                        candidate={sampleCandidate}
                        selectedColumns={new Set(columns)}
                        onToggleColumns={(cols) => toggleColumnsForGroup(group.id, cols)}
                      />
                    ) : group.entityType === 'manager' ? (
                      <ManagerPickerSchematic
                        manager={sampleManager}
                        selectedColumns={new Set(columns)}
                        onToggleColumns={(cols) => toggleColumnsForGroup(group.id, cols)}
                      />
                    ) : (
                      <CompanyPickerSchematic
                        company={sampleCompany}
                        selectedColumns={new Set(columns)}
                        onToggleColumns={(cols) => toggleColumnsForGroup(group.id, cols)}
                      />
                    )}

                    {/* + more fields expander for the long tail */}
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <button
                        type="button"
                        onClick={() => setShowMoreFields(prev => ({ ...prev, [group.id]: !prev[group.id] }))}
                        className="text-[11px] font-medium text-blue-600 hover:text-blue-800"
                      >
                        {showMoreFields[group.id] ? '− hide other fields' : '+ more fields'}
                      </button>
                      {showMoreFields[group.id] && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {(group.entityType === 'candidate'
                              ? CANDIDATE_LONG_TAIL_COLUMNS
                              : group.entityType === 'manager'
                                ? MANAGER_LONG_TAIL_COLUMNS
                                : COMPANY_LONG_TAIL_COLUMNS)
                            .filter(c =>
                              group.entityType === 'candidate'
                                ? !CANDIDATE_PICKER_COLUMNS.includes(c)
                                : group.entityType === 'manager'
                                  ? !MANAGER_PICKER_COLUMNS.includes(c)
                                  : !COMPANY_PICKER_COLUMNS.includes(c)
                            )
                            .map(col => {
                              const selected = columns.includes(col);
                              return (
                                <button
                                  key={col}
                                  type="button"
                                  onClick={() => toggleColumn(group.id, col)}
                                  className={`px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${
                                    selected
                                      ? 'bg-amber-50 text-amber-800 border-amber-300'
                                      : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300 hover:bg-amber-50/40'
                                  }`}
                                >
                                  {formatColumnLabel(col)} {selected && '✕'}
                                </button>
                              );
                            })}

                          {/* Also surface any AI-picked columns that aren't in
                              the picker schematic AND aren't in our long-tail
                              list, so the user can still see/remove them. */}
                          {columns
                            .filter(c =>
                              group.entityType === 'candidate'
                                ? !CANDIDATE_PICKER_COLUMNS.includes(c) && !CANDIDATE_LONG_TAIL_COLUMNS.includes(c)
                                : !COMPANY_PICKER_COLUMNS.includes(c) && !COMPANY_LONG_TAIL_COLUMNS.includes(c)
                            )
                            .map(col => (
                              <button
                                key={col}
                                type="button"
                                onClick={() => toggleColumn(group.id, col)}
                                className="px-2 py-0.5 rounded-full text-[11px] font-medium border bg-amber-50 text-amber-800 border-amber-300"
                              >
                                {formatColumnLabel(col)} ✕
                              </button>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Fallback: any entity type without a dedicated schematic
                  (theoretically none after manager support) keeps the old
                  flat chip list. */}
              {(group.entityType !== 'candidate' && group.entityType !== 'company' && group.entityType !== 'manager') && columns.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-1.5">Searching in</p>
                  <div className="flex flex-wrap gap-1.5">
                    {columns.map(col => (
                      <button
                        key={col}
                        onClick={() => toggleColumn(group.id, col)}
                        className="px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                      >
                        {formatColumnLabel(col)} ✕
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Read-only advanced rules */}
              {nonKeyword.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-1.5">Other rules</p>
                  <ul className="text-xs text-gray-600 space-y-0.5">
                    {nonKeyword.map(c => (
                      <li key={c.id}>
                        <span className="font-mono">{formatColumnLabel(c.column)}</span>{' '}
                        {c.operator.replace(/_/g, ' ')}{' '}
                        {c.values ? <em>"{c.values.join('" + "')}" (all in same field)</em> : <em>"{c.value}"</em>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}

          {radiusBlocks.length > 0 && (
            <div className="mb-5 p-4 border border-gray-200 rounded-xl bg-amber-50/40">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Location filters</h3>
              <ul className="text-xs text-gray-700 space-y-1">
                {radiusBlocks.map((b, i) => (
                  <li key={i}>
                    <strong>{b.label}:</strong> within {b.r.distance} miles of {b.r.location}
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-gray-400 mt-1">Location filters aren't editable here yet.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            data-tour="ai-preview-run"
            onClick={handleApply}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm"
          >
            Run Search →
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AISearchPreviewModal;
